import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close,
  Delete as DeleteIcon,
  Add as AddIcon,
  Inventory2 as InventoryIcon,
  Warning
} from '@mui/icons-material';

const ProductSlabModal = ({
  open,
  onClose,
  onSave,
  products = [],
  items = [],
  categories = [],
  loading = false,
  editProductId = null,
  editRelations = [],
  existingProductIds = []
}) => {
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [productRelations, setProductRelations] = useState([]);
  const [originalRelations, setOriginalRelations] = useState([]); // Track original data
  const [showGetItemsModal, setShowGetItemsModal] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const loadedProductIdRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      // Load edit data if provided and not already loaded
      if (editProductId && loadedProductIdRef.current !== editProductId) {
        loadedProductIdRef.current = editProductId;
        setSelectedProductId(editProductId);
        const relations = editRelations.map(rel => ({
          Id: rel.Id,
          ProductItemId: rel.ProductItemId,
          Quantity: rel.Quantity || 0,
          Rate: rel.Rate || 0,
          IsActive: true
        }));
        setProductRelations(relations);
        setOriginalRelations(JSON.parse(JSON.stringify(relations))); // Deep copy
      } else if (!editProductId && loadedProductIdRef.current !== null) {
        loadedProductIdRef.current = null;
        setSelectedProductId('');
        setSelectedItems([]);
        setProductRelations([]);
        setOriginalRelations([]);
        setShowGetItemsModal(false);
      }
    } else {
      loadedProductIdRef.current = null;
      setSelectedProductId('');
      setSelectedItems([]);
      setProductRelations([]);
      setOriginalRelations([]);
      setShowGetItemsModal(false);
    }
  }, [open, editProductId]);

  const handleGetItems = () => {
    setShowGetItemsModal(true);
  };

  const handleAddSelectedItems = () => {
    const newRelations = selectedItems
      .filter(itemId => !productRelations.find(rel => rel.ProductItemId === itemId))
      .map(itemId => {
        const item = items.find(i => i.Id === itemId);
        return {
          ProductItemId: itemId,
          Quantity: 1, // Default quantity = 1
          Rate: item?.SaleRate || 0, // Auto-fetch from item's SaleRate
          IsActive: true
        };
      });

    setProductRelations(prev => [...prev, ...newRelations]);
    setSelectedItems([]);
    setShowGetItemsModal(false);
  };

  const handleItemCheckbox = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleQuantityChange = (itemId, value) => {
    setProductRelations(prev =>
      prev.map(rel =>
        rel.ProductItemId === itemId
          ? { ...rel, Quantity: parseFloat(value) || 0 }
          : rel
      )
    );
  };

  const handleRateChange = (itemId, value) => {
    setProductRelations(prev =>
      prev.map(rel =>
        rel.ProductItemId === itemId
          ? { ...rel, Rate: parseFloat(value) || 0 }
          : rel
      )
    );
  };

  const handleDeleteRelation = (productItemId) => {
    setProductRelations(prev => prev.filter(rel => rel.ProductItemId !== productItemId));
  };

  const handleApply = () => {
    if (!selectedProductId) {
      setShowValidationDialog(true);
      return;
    }
    
    // Check for duplicate product slab (only in create mode)
    if (!editProductId && existingProductIds.includes(parseInt(selectedProductId))) {
      setShowDuplicateDialog(true);
      return;
    }
    
    console.log('Submitting all relations:', productRelations);
    onSave(selectedProductId, productRelations);
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.Id === itemId);
    return item ? item.Name : '-';
  };

  const getCategoryName = (itemId) => {
    const item = items.find(i => i.Id === itemId);
    if (!item) return '-';
    // Use CategoryName from response if available, otherwise lookup
    return item.CategoryName || categories.find(c => c.Id === item.CategoryId)?.Name || '-';
  };

  const getItemBarCode = (itemId) => {
    const item = items.find(i => i.Id === itemId);
    return item ? item.BarCode || '-' : '-';
  };

  // Filter items where IsProduct = false
  const nonProductItems = items.filter(item => item.IsProduct === false);

  // Filter products where IsProduct = true
  const productItems = products.filter(product => product.IsProduct === true);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '900px',
            maxWidth: '95vw',
            height: 'auto',
            maxHeight: isMobile ? '100vh' : '90vh',
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          p: '10px 16px', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InventoryIcon sx={{ fontSize: '1.1rem' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                Related Products Setup
              </h2>
            </div>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: 'white' }}
            >
              <Close sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent sx={{ p: 2, pt: 2.5, minHeight: '400px' }}>
          <div>
            {/* Product Selection */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1, mt: '20px' }}>
              Select Product
            </Typography>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px', 
              alignItems: 'flex-start' 
            }}>
              <div style={{ flex: 2, width: isMobile ? '100%' : 'auto' }}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    label="Product"
                    disabled={loading || editProductId !== null}
                  >
                    <MenuItem value="">
                      <em>Select a product</em>
                    </MenuItem>
                    {productItems.map(product => (
                      <MenuItem key={product.Id} value={product.Id}>
                        {product.Name} {product.BarCode ? `(${product.BarCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '150px', width: isMobile ? '100%' : 'auto' }}>
                <Button
                  variant="contained"
                  onClick={handleGetItems}
                  disabled={!selectedProductId || loading}
                  size="small"
                  sx={{ 
                    height: '40px',
                    width: '100%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                    }
                  }}
                >
                  Get Items
                </Button>
              </div>
            </div>

            {/* Product Components Table */}
            {productRelations.length > 0 && (
              <div style={{ marginTop: '20px', overflowX: isMobile ? 'auto' : 'visible' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1 }}>
                  Product Components
                </Typography>
                <TableContainer 
                  component={Paper}
                  sx={{ 
                    border: '1px solid #e2e8f0',
                    overflowX: 'auto',
                    '& .MuiTableCell-root': {
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      padding: isMobile ? '4px 6px' : '5px 8px',
                      height: '30px'
                    }
                  }}
                >
                  <Table size="small" sx={{ minWidth: isMobile ? '500px' : 'auto' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ width: '50%', height: '30px' }}>Item Name</TableCell>
                        <TableCell sx={{ width: '20%', height: '30px' }}>Quantity</TableCell>
                        <TableCell sx={{ width: '20%', height: '30px' }}>Rate (AED)</TableCell>
                        <TableCell sx={{ width: '10%', height: '30px', textAlign: 'center' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productRelations.map(relation => (
                        <TableRow key={relation.ProductItemId} hover>
                          <TableCell sx={{ width: '50%' }}>
                            {getItemName(relation.ProductItemId)}
                          </TableCell>
                          <TableCell sx={{ width: '20%' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={relation.Quantity}
                              onChange={(e) => handleQuantityChange(relation.ProductItemId, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ 
                                width: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '0.75rem',
                                  padding: '4px 8px',
                                  textAlign: 'right'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '20%' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={relation.Rate}
                              onChange={(e) => handleRateChange(relation.ProductItemId, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ 
                                width: '100px',
                                '& .MuiInputBase-input': {
                                  fontSize: '0.75rem',
                                  padding: '4px 8px',
                                  textAlign: 'right'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRelation(relation.ProductItemId)}
                              sx={{ 
                                p: 0.5, 
                                color: '#dc2626',
                                '&:hover': { bgcolor: '#fee2e2' }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={onClose} variant="outlined" size="small">
            Cancel
          </Button>
          <Button 
            onClick={handleApply} 
            variant="contained" 
            size="small"
            disabled={!selectedProductId || productRelations.length === 0 || loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={showGetItemsModal}
        onClose={() => setShowGetItemsModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          p: '8px 14px', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '40px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InventoryIcon sx={{ fontSize: '1rem' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Select Items</span>
          </div>
          <IconButton
            onClick={() => setShowGetItemsModal(false)}
            sx={{ 
              color: 'white',
              padding: '2px',
              '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
            }}
            size="small"
          >
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px',
            padding: '10px 14px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.875rem' }}>
              Select Items to Add to Product
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleAddSelectedItems}
              disabled={selectedItems.length === 0}
              startIcon={<AddIcon sx={{ fontSize: '0.95rem' }} />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                px: 2.5,
                py: 0.75,
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0px)'
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Add Selected ({selectedItems.length})
            </Button>
          </div>

          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: '350px', 
              overflow: 'auto',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
              '& .MuiTableCell-root': {
                fontSize: '0.8rem',
                padding: '4px 12px',
                height: '27px'
              }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    padding="checkbox"
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      height: '27px',
                      py: 0
                    }}
                  >
                    <Checkbox
                      size="small"
                      indeterminate={
                        selectedItems.length > 0 && 
                        selectedItems.length < nonProductItems.filter(item => 
                          !productRelations.some(rel => rel.ProductItemId === item.Id)
                        ).length
                      }
                      checked={
                        nonProductItems.filter(item => 
                          !productRelations.some(rel => rel.ProductItemId === item.Id)
                        ).length > 0 && 
                        selectedItems.length === nonProductItems.filter(item => 
                          !productRelations.some(rel => rel.ProductItemId === item.Id)
                        ).length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const availableItems = nonProductItems
                            .filter(item => !productRelations.some(rel => rel.ProductItemId === item.Id))
                            .map(item => item.Id);
                          setSelectedItems(availableItems);
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    width: '60px',
                    height: '27px',
                    py: 0
                  }}>
                    Sl.No
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    width: '40%',
                    height: '27px',
                    py: 0
                  }}>
                    Item Name
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    width: '30%',
                    height: '27px',
                    py: 0
                  }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    textAlign: 'right',
                    width: '20%',
                    height: '27px',
                    py: 0
                  }}>
                    Rate (AED)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nonProductItems.length > 0 ? (
                  nonProductItems.map((item, index) => {
                    const isAlreadyAdded = productRelations.some(rel => rel.ProductItemId === item.Id);
                    return (
                      <TableRow 
                        key={item.Id} 
                        hover={!isAlreadyAdded}
                        sx={{ 
                          cursor: isAlreadyAdded ? 'not-allowed' : 'pointer',
                          opacity: isAlreadyAdded ? 0.5 : 1,
                          backgroundColor: isAlreadyAdded ? '#f3f4f6' : 'inherit',
                          '&:hover': {
                            backgroundColor: isAlreadyAdded ? '#f3f4f6' : '#f0f9ff'
                          }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedItems.includes(item.Id)}
                            onChange={() => handleItemCheckbox(item.Id)}
                            disabled={isAlreadyAdded}
                            sx={{
                              color: isAlreadyAdded ? '#cbd5e1' : '#64748b',
                              '&.Mui-checked': {
                                color: '#667eea'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 500 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ color: '#1e293b', fontWeight: 500 }}>
                          {item.Name}
                        </TableCell>
                        <TableCell sx={{ color: '#475569' }}>
                          {item.CategoryName || categories.find(c => c.Id === item.CategoryId)?.Name || '-'}
                        </TableCell>
                        <TableCell sx={{ color: '#0f766e', fontWeight: 600, textAlign: 'right' }}>
                          <span style={{ fontSize: '10px', color: '#666' }}>AED </span>
                          {item.SaleRate?.toLocaleString() || 0}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: '#6b7280', py: 3 }}>
                      No items available. Please create items with IsProduct = false first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ 
          px: 2.5, 
          py: 2, 
          bgcolor: 'white', 
          borderTop: '1px solid #e5e7eb',
          gap: 1.5
        }}>
          <Button 
            onClick={() => setShowGetItemsModal(false)} 
            variant="outlined" 
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              borderColor: '#d1d5db',
              color: '#6b7280',
              px: 2.5,
              py: 0.75,
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#9ca3af',
                background: '#f9fafb',
                color: '#374151'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSelectedItems} 
            variant="contained" 
            size="small"
            disabled={selectedItems.length === 0}
            startIcon={<AddIcon sx={{ fontSize: '0.95rem' }} />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              px: 2.5,
              py: 0.75,
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0px)'
              },
              '&:disabled': {
                background: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Add Selected ({selectedItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Product Dialog */}
      <Dialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: '12px 16px', 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Warning sx={{ fontSize: '1.2rem' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Duplicate Product Slab</span>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography sx={{ color: '#374151', fontSize: '0.9rem', lineHeight: 1.6 }}>
            This product already has a slab configured. Please edit the existing slab instead.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ 
          px: 2.5, 
          py: 2, 
          bgcolor: '#f9fafb', 
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button 
            onClick={() => setShowDuplicateDialog(false)} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 3,
              py: 1,
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: '12px 16px', 
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Warning sx={{ fontSize: '1.2rem' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Validation Error</span>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography sx={{ color: '#374151', fontSize: '0.9rem', lineHeight: 1.6 }}>
            Please select a product before proceeding.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ 
          px: 2.5, 
          py: 2, 
          bgcolor: '#f9fafb', 
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button 
            onClick={() => setShowValidationDialog(false)} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 3,
              py: 1,
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductSlabModal;
